// Customer Portal — types shared across UI + data layer

export type PortalRole = "owner" | "event_manager" | "finance" | "safety" | "readonly";

export const PORTAL_ROLE_LABEL: Record<PortalRole, string> = {
  owner:         "Organisation Owner",
  event_manager: "Event Manager",
  finance:       "Finance Contact",
  safety:        "Safety Officer",
  readonly:      "Read Only",
};

export const PORTAL_ROLE_DESCRIPTION: Record<PortalRole, string> = {
  owner:         "Full organisation access, user management, invoices, plans, event approvals.",
  event_manager: "Manage assigned events, upload documents, approve plans, communicate with OpsCom.",
  finance:       "Invoices, payments and statements only.",
  safety:        "Medical plans, RAMS, incident summaries, compliance documents.",
  readonly:      "View assigned documents and events only.",
};

// What each role is allowed to do — used by canPortal() helper.
export type PortalCapability =
  | "events.read" | "events.create" | "events.update" | "events.approve"
  | "documents.read" | "documents.upload" | "documents.approve" | "documents.delete"
  | "messages.read" | "messages.send"
  | "invoices.read" | "invoices.pay"
  | "users.read"  | "users.manage"
  | "live.view"
  | "compliance.view"
  | "audit.view";

export const PORTAL_PERMISSIONS: Record<PortalRole, PortalCapability[]> = {
  owner: [
    "events.read","events.create","events.update","events.approve",
    "documents.read","documents.upload","documents.approve","documents.delete",
    "messages.read","messages.send",
    "invoices.read","invoices.pay",
    "users.read","users.manage",
    "live.view",
    "compliance.view",
    "audit.view",
  ],
  event_manager: [
    "events.read","events.create","events.update","events.approve",
    "documents.read","documents.upload",
    "messages.read","messages.send",
    "invoices.read",
    "live.view",
    "compliance.view",
  ],
  finance: [
    "events.read",
    "invoices.read","invoices.pay",
    "messages.read",
  ],
  safety: [
    "events.read",
    "documents.read","documents.upload","documents.approve",
    "messages.read","messages.send",
    "live.view",
    "compliance.view",
  ],
  readonly: [
    "events.read",
    "documents.read",
    "messages.read",
  ],
};

export function canPortal(role: PortalRole | undefined, cap: PortalCapability): boolean {
  if (!role) return false;
  return PORTAL_PERMISSIONS[role]?.includes(cap) ?? false;
}

// ── Service catalogue ────────────────────────────────────────────────────────
export type ServiceType =
  | "event_medical" | "fire_cover" | "rescue" | "water_rescue" | "rope_rescue"
  | "safety_officer" | "incident_commander" | "ambulance" | "control_room"
  | "welfare" | "specialist_vehicle" | "drone" | "major_incident" | "eoc_unit";

export interface ServiceCatalogEntry {
  id: ServiceType;
  label: string;
  shortLabel: string;
  category: "medical" | "fire_rescue" | "command" | "support";
  description: string;
  icon: string; // lucide icon name
  defaultLevels?: string[];
  unitRate?: number; // £ per hour, indicative
}

export const SERVICE_CATALOG: ServiceCatalogEntry[] = [
  { id: "event_medical",      label: "Event Medical Cover",         shortLabel: "Medical",       category: "medical",     description: "On-site medical teams scaled to event risk profile.",            icon: "Stethoscope", defaultLevels: ["First Aid","BLS","ILS","ALS","Doctor + Paramedic"], unitRate: 28 },
  { id: "ambulance",          label: "Ambulance Provision",         shortLabel: "Ambulance",     category: "medical",     description: "Front-line emergency ambulance with crew and standby capacity.",  icon: "Truck",       defaultLevels: ["DCA Crew","ALS DCA","Critical Care"],                unitRate: 75 },
  { id: "welfare",            label: "Welfare / First Aid Posts",   shortLabel: "Welfare",       category: "medical",     description: "Welfare points, hydration, mental health support and minor care.",icon: "Heart",                                                                              unitRate: 22 },
  { id: "fire_cover",         label: "Fire Cover",                  shortLabel: "Fire",          category: "fire_rescue", description: "Fire safety officers, BA teams and firefighting capability.",     icon: "Flame",       defaultLevels: ["Fire Wardens","BA Team","Industrial Fire"],          unitRate: 65 },
  { id: "rescue",             label: "Rescue Teams",                shortLabel: "Rescue",        category: "fire_rescue", description: "Confined space, USAR and general rescue capability.",             icon: "LifeBuoy",                                                                          unitRate: 70 },
  { id: "water_rescue",       label: "Water Rescue",                shortLabel: "Water Rescue",  category: "fire_rescue", description: "Swift-water and flood rescue technicians and craft.",             icon: "Waves",                                                                             unitRate: 80 },
  { id: "rope_rescue",        label: "Rope Rescue",                 shortLabel: "Rope Rescue",   category: "fire_rescue", description: "Working at height and technical rope rescue specialists.",        icon: "Anchor",                                                                            unitRate: 85 },
  { id: "incident_commander", label: "Incident Commanders",         shortLabel: "Incident Cmdr", category: "command",     description: "Silver/Gold-trained commanders for tactical coordination.",       icon: "Shield",                                                                            unitRate: 90 },
  { id: "safety_officer",     label: "Safety Officers",             shortLabel: "Safety",        category: "command",     description: "NEBOSH/IOSH event safety officers and SAG liaison.",              icon: "ShieldCheck",                                                                       unitRate: 55 },
  { id: "control_room",       label: "Control Room Support",        shortLabel: "Control Room",  category: "command",     description: "Dedicated control room operators and dispatchers.",                icon: "Radio",                                                                             unitRate: 40 },
  { id: "drone",              label: "Drone Support",               shortLabel: "Drone",         category: "support",     description: "CAA-licensed drone teams for aerial overwatch and mapping.",      icon: "Plane",                                                                             unitRate: 60 },
  { id: "specialist_vehicle", label: "Specialist Vehicles",         shortLabel: "Specialist",    category: "support",     description: "4x4, ATV, mobile treatment units and bespoke vehicles.",         icon: "Car",                                                                                unitRate: 45 },
  { id: "eoc_unit",           label: "Temporary EOC / Command Unit",shortLabel: "EOC Unit",      category: "command",     description: "Mobile command units with welfare, comms and briefing space.",    icon: "Building2",                                                                         unitRate: 120 },
  { id: "major_incident",     label: "Major Incident Support",      shortLabel: "MI Support",    category: "command",     description: "JESIP-trained MI cell, METHANE coordinators and forward command.",icon: "Siren",                                                                             unitRate: 100 },
];

export const SERVICE_BY_ID: Record<ServiceType, ServiceCatalogEntry> = SERVICE_CATALOG.reduce(
  (acc, s) => { acc[s.id] = s; return acc; },
  {} as Record<ServiceType, ServiceCatalogEntry>
);

// ── Event ────────────────────────────────────────────────────────────────────
export type EventStatus =
  | "draft" | "submitted" | "quoted" | "accepted" | "planning"
  | "confirmed" | "live" | "stood_down" | "complete" | "cancelled";

export interface EventStatusCfg { label: string; dot: string; text: string; bg: string; border: string }
export const EVENT_STATUS_CFG: Record<EventStatus, EventStatusCfg> = {
  draft:      { label: "DRAFT",       dot: "bg-slate-500",   text: "text-slate-300",  bg: "bg-slate-800",    border: "border-slate-700" },
  submitted:  { label: "SUBMITTED",   dot: "bg-blue-500",    text: "text-blue-300",   bg: "bg-blue-950",     border: "border-blue-700" },
  quoted:     { label: "QUOTED",      dot: "bg-cyan-500",    text: "text-cyan-300",   bg: "bg-cyan-950",     border: "border-cyan-700" },
  accepted:   { label: "ACCEPTED",    dot: "bg-indigo-500",  text: "text-indigo-300", bg: "bg-indigo-950",   border: "border-indigo-700" },
  planning:   { label: "PLANNING",    dot: "bg-purple-500",  text: "text-purple-300", bg: "bg-purple-950",   border: "border-purple-700" },
  confirmed:  { label: "CONFIRMED",   dot: "bg-emerald-500", text: "text-emerald-300",bg: "bg-emerald-950",  border: "border-emerald-700" },
  live:       { label: "LIVE",        dot: "bg-red-500",     text: "text-red-300",    bg: "bg-red-950",      border: "border-red-700" },
  stood_down: { label: "STOOD DOWN",  dot: "bg-amber-500",   text: "text-amber-300",  bg: "bg-amber-950",    border: "border-amber-700" },
  complete:   { label: "COMPLETE",    dot: "bg-slate-400",   text: "text-slate-300",  bg: "bg-slate-800",    border: "border-slate-700" },
  cancelled:  { label: "CANCELLED",   dot: "bg-slate-600",   text: "text-slate-500",  bg: "bg-slate-900",    border: "border-slate-800" },
};

export type RiskLevel = "low" | "medium" | "high" | "very_high";
export const RISK_LEVEL_CFG: Record<RiskLevel, { label: string; cls: string }> = {
  low:       { label: "LOW",       cls: "bg-emerald-950 text-emerald-300 border-emerald-800" },
  medium:    { label: "MEDIUM",    cls: "bg-amber-950 text-amber-300 border-amber-800" },
  high:      { label: "HIGH",      cls: "bg-orange-950 text-orange-300 border-orange-800" },
  very_high: { label: "VERY HIGH", cls: "bg-red-950 text-red-300 border-red-800" },
};

export interface EventService {
  id: string;
  service_type: ServiceType;
  level?: string;
  quantity: number;
  notes?: string;
  status: "requested" | "quoted" | "confirmed" | "staffed" | "complete" | "cancelled";
  hours_estimate?: number;
  unit_rate?: number;
  line_total?: number;
}

export interface PortalEvent {
  id: string;
  ref: string;
  org_id: string;
  org_name?: string;
  title: string;
  event_type?: string;
  category?: "sport" | "music" | "community" | "corporate" | "motorsport" | "exhibition" | "other";
  starts_at?: string;
  ends_at?: string;
  venue_name?: string;
  venue_address?: string;
  postcode?: string;
  expected_attendance?: number;
  risk_level?: RiskLevel;
  status: EventStatus;
  approved_at?: string;
  notes?: string;
  services: EventService[];
  created_at: string;
  // Derived / aggregated:
  staffing_complete?: number; // 0..1
  plan_approved?: boolean;
  invoice_status?: "none" | "draft" | "sent" | "partial" | "paid" | "overdue";
  active_incidents?: number;
}

// ── Documents ────────────────────────────────────────────────────────────────
export type DocumentCategory =
  | "medical_plan" | "rams" | "site_map" | "safety_plan" | "sag" | "stewarding"
  | "evacuation" | "insurance" | "incident_report" | "post_event" | "contract" | "other";

export const DOCUMENT_CATEGORY_LABEL: Record<DocumentCategory, string> = {
  medical_plan:    "Medical Plan",
  rams:            "RAMS",
  site_map:        "Site Map",
  safety_plan:     "Safety Plan",
  sag:             "SAG Documents",
  stewarding:      "Stewarding Plan",
  evacuation:      "Evacuation Plan",
  insurance:       "Insurance",
  incident_report: "Incident Report",
  post_event:      "Post-Event Report",
  contract:        "Contract",
  other:           "Other",
};

export type DocApprovalStatus =
  | "draft" | "pending" | "client_approved" | "medical_approved"
  | "director_approved" | "rejected" | "superseded";

export const DOC_APPROVAL_CFG: Record<DocApprovalStatus, { label: string; cls: string }> = {
  draft:             { label: "DRAFT",             cls: "bg-slate-800 text-slate-400 border-slate-700" },
  pending:           { label: "PENDING REVIEW",    cls: "bg-amber-950 text-amber-300 border-amber-800" },
  client_approved:   { label: "CLIENT APPROVED",   cls: "bg-blue-950 text-blue-300 border-blue-800" },
  medical_approved:  { label: "MEDICAL APPROVED",  cls: "bg-purple-950 text-purple-300 border-purple-800" },
  director_approved: { label: "FULLY APPROVED",    cls: "bg-emerald-950 text-emerald-300 border-emerald-800" },
  rejected:          { label: "REJECTED",          cls: "bg-red-950 text-red-300 border-red-800" },
  superseded:        { label: "SUPERSEDED",        cls: "bg-slate-900 text-slate-500 border-slate-800" },
};

export interface PortalDocument {
  id: string;
  org_id?: string;
  event_id?: string;
  category: DocumentCategory;
  title: string;
  filename?: string;
  size_bytes?: number;
  version: number;
  visibility: "org" | "event" | "operational" | "restricted" | "public";
  approval_status: DocApprovalStatus;
  expires_at?: string;
  uploaded_by_name?: string;
  uploaded_by_role?: string;
  copy_number?: number;
  watermark_text?: string;
  acks?: { recipient_name: string; recipient_role?: string; required: boolean; opened_at?: string; downloaded_at?: string; acknowledged_at?: string }[];
  created_at: string;
}

// ── Messages ─────────────────────────────────────────────────────────────────
export interface PortalMessage {
  id: string;
  event_id: string;
  thread: string;
  sender_kind: "client" | "opscom" | "system";
  sender_name: string;
  sender_role?: string;
  body: string;
  priority: "normal" | "operational" | "urgent";
  read_at?: string;
  created_at: string;
}

// ── Org / User ───────────────────────────────────────────────────────────────
export interface PortalOrg {
  id: string;
  name: string;
  trading_name?: string;
  address?: string;
  postcode?: string;
  primary_contact_name?: string;
  primary_contact_email?: string;
  primary_contact_phone?: string;
  status: "active" | "suspended" | "archived" | "prospect";
  brand_logo_url?: string;
  brand_primary_color?: string;
}

export interface PortalUser {
  id: string;
  org_id: string;
  email: string;
  name: string;
  phone?: string;
  role: PortalRole;
  status: "active" | "invited" | "suspended";
  last_login_at?: string;
}

// ── Timeline ─────────────────────────────────────────────────────────────────
export type TimelineKind =
  | "submitted" | "quote_issued" | "quote_accepted" | "plan_uploaded" | "plan_approved"
  | "staffing_confirmed" | "authorised" | "event_live" | "sitrep" | "incident"
  | "stand_down" | "complete" | "message" | "document" | "status_change" | "note";

export interface TimelineEntry {
  id: string;
  event_id: string;
  kind: TimelineKind;
  title: string;
  detail?: string;
  actor_name?: string;
  actor_role?: string;
  created_at: string;
}

export const TIMELINE_KIND_CFG: Record<TimelineKind, { label: string; color: string; icon: string }> = {
  submitted:          { label: "Booking Submitted",     color: "text-blue-400",    icon: "Send" },
  quote_issued:       { label: "Quote Issued",          color: "text-cyan-400",    icon: "FileText" },
  quote_accepted:     { label: "Quote Accepted",        color: "text-emerald-400", icon: "CheckCircle2" },
  plan_uploaded:      { label: "Plan Uploaded",         color: "text-purple-400",  icon: "Upload" },
  plan_approved:      { label: "Plan Approved",         color: "text-emerald-400", icon: "ShieldCheck" },
  staffing_confirmed: { label: "Staffing Confirmed",    color: "text-emerald-400", icon: "Users" },
  authorised:         { label: "Deployment Authorised", color: "text-emerald-400", icon: "CheckSquare" },
  event_live:         { label: "Event Live",            color: "text-red-400",     icon: "Radio" },
  sitrep:             { label: "SitRep",                color: "text-indigo-400",  icon: "MessageSquare" },
  incident:           { label: "Incident Logged",       color: "text-amber-400",   icon: "AlertTriangle" },
  stand_down:         { label: "Stood Down",            color: "text-emerald-400", icon: "StopCircle" },
  complete:           { label: "Complete",              color: "text-slate-400",   icon: "Archive" },
  message:            { label: "Message",               color: "text-slate-300",   icon: "MessageCircle" },
  document:           { label: "Document",              color: "text-slate-300",   icon: "FileText" },
  status_change:      { label: "Status Change",         color: "text-slate-300",   icon: "Activity" },
  note:               { label: "Note",                  color: "text-slate-400",   icon: "Edit3" },
};
